import { Children, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import './category-workspace.css';

const STORAGE_KEY = 'longjie.dealer-category-width';
const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 220;
const MAX_WIDTH = 640;
const CONTENT_MIN_WIDTH = 320;
const HANDLE_WIDTH = 22;
const WIDTH_CHANGE_EVENT = 'longjie-category-width-changed';

function savedWidth(storageKey: string, defaultWidth: number, minWidth: number) {
  try {
    const saved = window.localStorage.getItem(storageKey);
    const width = saved === null ? defaultWidth : Number(saved);
    return Number.isFinite(width) ? Math.max(minWidth, Math.min(MAX_WIDTH, width)) : defaultWidth;
  } catch { return defaultWidth; }
}

export function DealerCatalogLayout({
  enabled,
  children,
  sidebarId = 'dealer-category-panel',
  contentId = 'dealer-product-panel',
  storageKey = STORAGE_KEY,
  defaultWidth = DEFAULT_WIDTH,
  minWidth = MIN_WIDTH,
  contentMinWidth = CONTENT_MIN_WIDTH,
  className = ''
}: {
  enabled: boolean;
  children: ReactNode;
  sidebarId?: string;
  contentId?: string;
  storageKey?: string;
  defaultWidth?: number;
  minWidth?: number;
  contentMinWidth?: number;
  className?: string;
}) {
  const workspaceRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; startWidth: number; width: number } | null>(null);
  const [preferredWidth, setPreferredWidth] = useState(() => savedWidth(storageKey, defaultWidth, minWidth));
  const [workspaceWidth, setWorkspaceWidth] = useState(0);
  const [dragging, setDragging] = useState(false);
  const maxWidth = Math.max(minWidth, Math.min(MAX_WIDTH, (workspaceWidth || 1082) - contentMinWidth - HANDLE_WIDTH));
  const width = Math.min(preferredWidth, maxWidth);

  useEffect(() => {
    if (!enabled) return;
    const synchronize = () => {
      if (!dragRef.current) setPreferredWidth(savedWidth(storageKey, defaultWidth, minWidth));
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === storageKey) synchronize();
    };
    synchronize();
    window.addEventListener('storage', onStorage);
    window.addEventListener(WIDTH_CHANGE_EVENT, synchronize);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(WIDTH_CHANGE_EVENT, synchronize);
    };
  }, [enabled, storageKey, defaultWidth, minWidth]);

  useEffect(() => {
    if (!enabled || !workspaceRef.current) return;
    const workspace = workspaceRef.current;
    setWorkspaceWidth(workspace.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWorkspaceWidth(entry.contentRect.width);
    });
    observer.observe(workspace);
    return () => observer.disconnect();
  }, [enabled]);

  function changeWidth(value: number, persist = false) {
    const next = Math.round(Math.max(minWidth, Math.min(maxWidth, value)));
    setPreferredWidth(next);
    if (persist) {
      try {
        window.localStorage.setItem(storageKey, String(next));
        window.dispatchEvent(new Event(WIDTH_CHANGE_EVENT));
      } catch { /* Resizing still works without storage. */ }
    }
    return next;
  }

  function finishDrag() {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    changeWidth(drag.width, true);
    setDragging(false);
  }

  if (!enabled) return <div>{children}</div>;
  const [sidebar, content] = Children.toArray(children);
  return <div ref={workspaceRef} className={`dealer-catalog-workspace${className ? ` ${className}` : ''}${dragging ? ' is-resizing' : ''}`}
    style={{ '--category-sidebar-width': `${width}px` } as CSSProperties}>
    {sidebar}
    <div className="dealer-catalog-resizer" role="separator" tabIndex={0} aria-label="调整商品分类栏宽度"
      aria-orientation="vertical" aria-controls={`${sidebarId} ${contentId}`}
      aria-valuemin={minWidth} aria-valuemax={maxWidth} aria-valuenow={width} aria-valuetext={`${width} 像素`}
      title="左右拖动调整分类栏宽度；双击恢复默认；方向键微调"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: width, width };
        setDragging(true);
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        drag.width = changeWidth(drag.startWidth + event.clientX - drag.startX);
      }}
      onPointerUp={finishDrag} onPointerCancel={finishDrag} onLostPointerCapture={finishDrag}
      onDoubleClick={() => changeWidth(defaultWidth, true)}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 30 : 10;
        const next = event.key === 'ArrowLeft' ? width - step : event.key === 'ArrowRight' ? width + step
          : event.key === 'Home' ? minWidth : event.key === 'End' ? maxWidth : null;
        if (next === null) return;
        event.preventDefault();
        changeWidth(next, true);
      }}><span aria-hidden="true" /></div>
    {content}
  </div>;
}
